import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { MobileNavigation } from '../components/MobileNavigation';
import { Plus, Copy, Trash, Clock, MapPin, Users, CurrencyDollar, UsersThree, Calendar } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function AdminTemplates() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCreateSessionDialog, setShowCreateSessionDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sessionDate, setSessionDate] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    location_id: '',
    start_time: '',
    end_time: '',
    court: '',
    capacity: 25,
    price: 15.0,
    age_group: '9-11'
  });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [templatesRes, locationsRes] = await Promise.all([
        fetch(`${getApiBaseUrl()}/api/admin/templates`, {
          credentials: 'include'
        }),
        fetch(`${getApiBaseUrl()}/api/locations`, {
          credentials: 'include'
        })
      ]);
      
      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData);
      }
      
      if (locationsRes.ok) {
        const locationsData = await locationsRes.json();
        setLocations(locationsData);
      }
    } catch (error) {
      console.error('Load data error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.location_id) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const newTemplate = await response.json();
        // Add location info
        const location = locations.find(l => l.location_id === newTemplate.location_id);
        newTemplate.location = location;
        setTemplates([newTemplate, ...templates]);
        setShowAddDialog(false);
        setFormData({
          name: '',
          location_id: '',
          start_time: '',
          end_time: '',
          court: '',
          capacity: 25,
          price: 15.0,
          age_group: '9-11'
        });
        toast.success('Template created!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create template');
      }
    } catch (error) {
      console.error('Create template error:', error);
      toast.error('Failed to create template');
    }
  };
  
  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setTemplates(templates.filter(t => t.template_id !== templateId));
        toast.success('Template deleted');
      }
    } catch (error) {
      console.error('Delete template error:', error);
      toast.error('Failed to delete template');
    }
  };
  
  const createSessionFromTemplate = async () => {
    if (!sessionDate) {
      toast.error('Please select a date');
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/templates/${selectedTemplate.template_id}/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date: sessionDate })
      });
      
      if (response.ok) {
        toast.success('Session created from template!');
        setShowCreateSessionDialog(false);
        setSelectedTemplate(null);
        setSessionDate('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create session');
      }
    } catch (error) {
      console.error('Create session error:', error);
      toast.error('Failed to create session');
    }
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-templates">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-heading">SESSION TEMPLATES</h1>
              <p className="text-sm text-muted-foreground">
                {templates.length} template{templates.length !== 1 ? 's' : ''} saved
              </p>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-template-btn">
                  <Plus size={20} weight="bold" className="mr-2" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">CREATE TEMPLATE</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Template Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Saturday Morning 9-11"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Location</Label>
                    <Select value={formData.location_id} onValueChange={(v) => setFormData({...formData, location_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.location_id} value={loc.location_id}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>End Time</Label>
                      <Input
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Capacity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.capacity}
                        onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Age Group</Label>
                      <Select value={formData.age_group} onValueChange={(v) => setFormData({...formData, age_group: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="9-11">Ages 9-11</SelectItem>
                          <SelectItem value="12-14">Ages 12-14</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Price ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Court (Optional)</Label>
                      <Input
                        value={formData.court}
                        onChange={(e) => setFormData({...formData, court: e.target.value})}
                        placeholder="e.g., Court A"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 btn-primary">
                      Save Template
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6">
        {templates.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <Copy size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO TEMPLATES</h3>
              <p className="text-muted-foreground">
                Create templates to quickly add recurring sessions.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {templates.map((template) => (
              <Card key={template.template_id} className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-lg">{template.name}</h3>
                    <span className="text-xs px-2 py-1 bg-primary/10 rounded-full text-primary">
                      Ages {template.age_group}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock size={16} />
                      <span>{formatTime(template.start_time)} - {formatTime(template.end_time)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin size={16} />
                      <span>{template.location?.name || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users size={16} />
                      <span>{template.capacity} players</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CurrencyDollar size={16} />
                      <span>${template.price?.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-3 border-t border-border">
                    <Button
                      size="sm"
                      className="flex-1 btn-primary"
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowCreateSessionDialog(true);
                      }}
                    >
                      <Calendar size={16} className="mr-1" />
                      Create Session
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteTemplate(template.template_id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Create Session from Template Dialog */}
      <Dialog open={showCreateSessionDialog} onOpenChange={setShowCreateSessionDialog}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">CREATE SESSION</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedTemplate && (
              <>
                <div className="p-3 bg-muted/20 rounded-lg">
                  <p className="font-medium">{selectedTemplate.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatTime(selectedTemplate.start_time)} - {formatTime(selectedTemplate.end_time)} at {selectedTemplate.location?.name}
                  </p>
                </div>
                
                <div>
                  <Label>Select Date</Label>
                  <Input
                    type="date"
                    value={sessionDate}
                    onChange={(e) => setSessionDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCreateSessionDialog(false)} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={createSessionFromTemplate} 
                    className="flex-1 btn-primary"
                    disabled={!sessionDate}
                  >
                    Create
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
